'use client';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ArrowRight, BookOpen, ChevronDown, ChevronUp, Scale } from 'lucide-react';
import { CtaButton, Eyebrow } from '@/components/ds';
import { useSignupModeState } from '@/components/utils/SignupModeProvider';
import { officialSourcesCount } from '@/content/sources';
import { loadGsap } from '@/lib/gsapLazy';
import { sendGtmEvent } from '@/lib/gtm/gtm';
import { MOTION_OK, prefersReducedMotion } from '@/lib/motion';
import { NEWS_ARTICLE_ID } from '@/lib/news/newsUtils';
import { useAskAction } from './useNewsVisitor';

/** Chi lo chiude lo ritrova chiuso negli articoli successivi, fino a fine sessione. */
const COLLAPSED_KEY = 'tn_news_dock_collapsed';
/**
 * Sul telefono il dock aperto prende un quarto dello schermo: parte compatto e
 * si apre da solo una volta per pagina, quando la lettura è avviata — a meno
 * che il lettore non l'abbia già aperto o chiuso lui.
 */
const COMPACT_START_QUERY = '(max-width: 639.98px)';
/** Quante schermate scorrere prima dell'apertura da sola */
const AUTO_OPEN_AFTER_SCREENS = 1.5;
/** Lo spazio che il dock toglie al fondo pagina: il piè di pagina lo aggiunge sotto di sé. */
const SPACE_VAR = '--floating-cta-space';
/**
 * Sotto `lg` il dock prende tutto il fondo e la bolla di Customerly gli starebbe
 * sopra, sul bottone: finché è montato la si toglie. Con l'SDK non si può, perché
 * una `hide()` chiamata prima che il launcher sia caricato resta in coda e basta;
 * la regola vive e muore col dock, quindi non serve ricordarsi di rimetterla.
 */
const HIDE_CHAT_BELOW_LG =
  '@media (max-width: 1023.98px) { #customerly-container { display: none !important; } }';

const ITEM = '[data-dock-item]';

function readFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean) {
  try {
    if (on) window.sessionStorage.setItem(key, '1');
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage negato: all'articolo dopo il dock riparte da capo, pazienza
  }
}

const PROOFS = [
  { icon: Scale, text: 'Norme, prassi e giurisprudenza citate' },
  { icon: BookOpen, text: 'Ogni fonte si apre, come qui' },
] as const;

interface DockProps {
  slug: string;
}

const Dock = ({ slug }: DockProps) => {
  const action = useAskAction(slug, 'dock')(null);
  const [startsCompact] = useState(() => window.matchMedia(COMPACT_START_QUERY).matches);
  const [collapsed, setCollapsed] = useState(() => startsCompact || readFlag(COLLAPSED_KEY));
  const autoOpenPending = useRef(startsCompact);

  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLElement>(null);
  const progress = useRef<HTMLDivElement>(null);
  const sheen = useRef<HTMLDivElement>(null);

  /* Ingresso: il dock sale dal fondo come una lastra che si mette a fuoco,
     poi il contenuto entra in sequenza e un riflesso lo attraversa una volta.
     GSAP arriva a richiesta (`lib/gsapLazy`): intanto il pannello sta nascosto
     con un `opacity: 0` in linea, messo prima del primo disegno. */
  useLayoutEffect(() => {
    const element = panel.current;
    if (!element || prefersReducedMotion()) return;
    const items = Array.from(root.current?.querySelectorAll<HTMLElement>(ITEM) ?? []);
    element.style.opacity = '0';

    let cancelled = false;
    let mm: gsap.MatchMedia | undefined;
    loadGsap()
      .then(({ gsap }) => {
        if (cancelled) return;
        mm = gsap.matchMedia();
        mm.add(MOTION_OK, () => {
          gsap.set(element, { yPercent: 135, opacity: 0, scale: 0.94, filter: 'blur(10px)' });
          gsap.set(items, { opacity: 0, y: 12 });
          gsap.set(sheen.current, { xPercent: 0 });

          gsap
            .timeline({ delay: 0.9 })
            .to(element, {
              yPercent: 0,
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: 1.15,
              ease: 'expo.out',
              clearProps: 'filter,scale',
            })
            .to(
              items,
              { opacity: 1, y: 0, duration: 0.6, stagger: 0.07, ease: 'power3.out' },
              '-=0.8'
            )
            .to(sheen.current, { xPercent: 300, duration: 1.4, ease: 'power2.inOut' }, '-=0.45');
        });
      })
      .catch(() => {
        element.style.opacity = '';
      });

    return () => {
      cancelled = true;
      mm?.revert();
      element.style.opacity = '';
    };
  }, []);

  /* Aperto ↔ compatto. La misura di prima la prende `setView`, prima che React
     cambi il contenuto; qui, a DOM già nuovo, il pannello parte da quella e si
     allarga (o si stringe) fino alla sua, mentre il contenuto nuovo si posa.
     Quando si apre da solo lo accompagna il riflesso dell'ingresso. */
  const morph = useRef<{ width: number; height: number; trigger: 'manual' | 'auto' } | null>(null);
  useLayoutEffect(() => {
    const from = morph.current;
    const element = panel.current;
    if (!from || !element) return;
    morph.current = null;
    if (prefersReducedMotion()) return;
    const to = element.getBoundingClientRect();
    const items = Array.from(root.current?.querySelectorAll<HTMLElement>(ITEM) ?? []);

    let cancelled = false;
    let mm: gsap.MatchMedia | undefined;
    loadGsap().then(({ gsap }) => {
      if (cancelled) return;
      mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        gsap.fromTo(
          element,
          { width: from.width, height: from.height },
          {
            width: to.width,
            height: to.height,
            duration: 0.6,
            ease: 'expo.out',
            clearProps: 'width,height',
          }
        );
        gsap.fromTo(
          items,
          { opacity: 0, y: 8 },
          { opacity: 1, y: 0, duration: 0.45, stagger: 0.05, delay: 0.1, ease: 'power3.out' }
        );
        if (from.trigger === 'auto') {
          gsap.fromTo(
            sheen.current,
            { xPercent: 0 },
            { xPercent: 300, duration: 1.4, delay: 0.25, ease: 'power2.inOut' }
          );
        }
      });
    });
    return () => {
      cancelled = true;
      mm?.revert();
    };
  }, [collapsed]);

  const setView = useCallback(
    (nextCollapsed: boolean, trigger: 'manual' | 'auto') => {
      const rect = panel.current?.getBoundingClientRect();
      if (rect) morph.current = { width: rect.width, height: rect.height, trigger };
      // Aperto da solo o toccato dal lettore: da qui in poi decide lui
      autoOpenPending.current = false;
      if (trigger === 'manual') writeFlag(COLLAPSED_KEY, nextCollapsed);
      setCollapsed(nextCollapsed);
      sendGtmEvent({
        event: 'news_dock_toggle',
        news_slug: slug,
        news_dock_state: nextCollapsed ? 'collapsed' : 'expanded',
        news_dock_trigger: trigger,
      });
    },
    [slug]
  );

  // Sul telefono, una volta per pagina: avviata la lettura, il compatto si apre e dice perché provare
  useEffect(() => {
    if (!autoOpenPending.current) return;
    const onScroll = () => {
      if (!autoOpenPending.current) return;
      if (window.scrollY < window.innerHeight * AUTO_OPEN_AFTER_SCREENS) return;
      setView(false, 'auto');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [setView]);

  // La linea di lettura in cima al dock; a fine articolo il riflesso ripassa
  useEffect(() => {
    const article = document.getElementById(NEWS_ARTICLE_ID);
    if (!article) return;
    let frame = 0;
    let finished = false;
    const update = () => {
      frame = 0;
      if (!progress.current) return;
      const rect = article.getBoundingClientRect();
      const span = rect.height - window.innerHeight;
      const read = span > 0 ? Math.min(1, Math.max(0, -rect.top / span)) : 1;
      progress.current.style.transform = `scaleX(${read})`;
      if (read > 0.985 && !finished) {
        finished = true;
        if (!prefersReducedMotion() && sheen.current) {
          const target = sheen.current;
          loadGsap().then(({ gsap }) =>
            gsap.fromTo(
              target,
              { xPercent: 0 },
              { xPercent: 300, duration: 1.4, ease: 'power2.inOut' }
            )
          );
        }
      } else if (read < 0.9) {
        finished = false;
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Il fondo della pagina non deve finire sotto il dock: il piè di pagina si allunga
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const html = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      html.style.setProperty(SPACE_VAR, `${Math.ceil(entry.borderBoxSize[0].blockSize)}px`);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      html.style.removeProperty(SPACE_VAR);
    };
  }, []);

  const toggle = () => setView(!collapsed, 'manual');

  return (
    <div
      ref={root}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4 sm:pb-5 print:hidden"
    >
      <style>{HIDE_CHAT_BELOW_LG}</style>
      <aside
        ref={panel}
        aria-label="Prova Trovanorma"
        className={
          'pointer-events-auto relative w-full overflow-hidden border border-white/14 bg-surface-dark text-white ' +
          'shadow-[0_30px_70px_-14px_rgb(0_18_40/0.6),0_10px_28px_-10px_rgb(0_18_40/0.45)] ' +
          (collapsed
            ? 'max-w-[34rem] rounded-ds-xl'
            : 'max-w-[66rem] rounded-ds-2xl lg:max-w-[min(66rem,calc(100vw-12rem))]')
        }
      >
        {/* Luce e trama: lo stesso bagliore del pannello di chiusura, più la
            griglia dell'hero appena accennata */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -left-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgb(234_88_12/0.34),transparent_66%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgb(255_255_255/0.035)_1px,transparent_1px),linear-gradient(90deg,rgb(255_255_255/0.035)_1px,transparent_1px)] bg-[length:28px_28px] [mask-image:linear-gradient(90deg,#000,transparent_55%)]"
        />
        <div
          ref={sheen}
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-[linear-gradient(100deg,transparent,rgb(255_255_255/0.13),transparent)]"
        />

        {/* Quanto si è letto dell'articolo */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-white/8">
          <div
            ref={progress}
            style={{ transform: 'scaleX(0)' }}
            className="h-full origin-left bg-[linear-gradient(90deg,rgb(255_255_255/0.25),rgb(255_255_255/0.9))] shadow-[0_0_12px_rgb(255_255_255/0.55)]"
          />
        </div>

        {collapsed ? (
          <div className="relative flex items-center gap-3 py-2.5 pr-2.5 pl-3 sm:pl-3.5">
            {/* Tutto il lato sinistro apre il dock: sul telefono non c'è posto
                per un bottone in più accanto alla CTA */}
            <button
              data-dock-item
              type="button"
              onClick={toggle}
              aria-expanded={false}
              className="-my-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-ds-sm py-1 pr-1 text-left ds-focus"
            >
              <Image
                src="/images/logo-icon-orange.svg"
                alt=""
                width={24}
                height={24}
                className="h-6 w-6 shrink-0"
              />
              {/* Le parole si misurano sulla colonna, non sullo schermo: quanto
                  spazio resta dipende anche dall'etichetta del bottone, e
                  «Prenota una demo» ne prende più di «Prova gratis» */}
              <span className="@container min-w-0 flex-1">
                <span className="flex items-center gap-1">
                  <span className="truncate text-[0.84375rem] leading-[1.25] font-semibold text-white">
                    <span className="@min-[8.625rem]:hidden">Caso simile?</span>
                    <span className="hidden @min-[8.625rem]:inline">Hai un caso simile?</span>
                  </span>
                  <ChevronUp className="h-3.5 w-3.5 shrink-0 text-white/45" aria-hidden="true" />
                </span>
                <span className="mt-0.5 block truncate text-[0.75rem] leading-[1.3] text-white/60">
                  <span className="@min-[8.25rem]:hidden">Con le fonti citate</span>
                  <span className="hidden @min-[8.25rem]:inline">Risposte con fonti citate</span>
                </span>
              </span>
            </button>
            <div data-dock-item className="shrink-0">
              <CtaButton
                href={action.href}
                onClick={action.onClick}
                size="sm"
                className="px-4 py-2.5 sm:px-5"
              >
                {action.label}
              </CtaButton>
            </div>
          </div>
        ) : (
          <div className="relative flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:gap-5 sm:py-4.5 sm:pr-4 sm:pl-5">
            {/* Il marchio, con la sua luce */}
            <div
              data-dock-item
              className="relative hidden h-13 w-13 shrink-0 place-items-center rounded-ds-lg border border-white/12 bg-white/[0.06] md:grid"
            >
              <span
                aria-hidden
                className="absolute inset-1 rounded-full bg-[radial-gradient(circle,rgb(234_88_12/0.45),transparent_70%)] blur-md"
              />
              <Image
                src="/images/logo-icon-orange.svg"
                alt=""
                width={26}
                height={26}
                className="relative h-6.5 w-6.5"
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div data-dock-item className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
                    <Eyebrow as="span" className="text-[0.6875rem] tracking-[0.16em]">
                      Prova Trovanorma
                    </Eyebrow>
                    <span className="hidden items-center gap-1.5 text-[0.75rem] text-white/55 md:inline-flex">
                      <span aria-hidden className="ds-live-dot h-1.5 w-1.5 shrink-0 rounded-full" />
                      {officialSourcesCount} fonti ufficiali, aggiornate ogni giorno
                    </span>
                  </div>
                  <p
                    data-dock-item
                    className="mt-1.5 font-display text-[1.03125rem] leading-[1.3] font-semibold tracking-[-0.015em] text-balance text-white sm:text-[1.125rem]"
                  >
                    La notizia è uguale per tutti. Il caso del tuo cliente no.
                  </p>
                </div>
                {/* Su mobile il bottone per chiudere sta in alto, accanto al titolo */}
                <button
                  data-dock-item
                  type="button"
                  onClick={toggle}
                  aria-expanded
                  aria-label="Riduci"
                  className="-mt-1 -mr-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-ds-sm text-white/50 ds-focus hover:bg-white/8 hover:text-white sm:hidden"
                >
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <p
                data-dock-item
                className="mt-1.5 text-[0.8125rem] leading-[1.45] text-pretty text-white/68 md:hidden"
              >
                Risposte con norme e sentenze citate: ogni fonte si apre, come in questo articolo.
              </p>
              <ul
                data-dock-item
                className="mt-2.5 hidden flex-wrap gap-x-5 gap-y-1.5 text-[0.8125rem] text-white/68 md:flex"
              >
                {PROOFS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 text-white/40" aria-hidden="true" />
                    {text}
                  </li>
                ))}
              </ul>
            </div>

            <div data-dock-item className="flex shrink-0 flex-col items-stretch sm:items-center">
              <CtaButton
                href={action.href}
                onClick={action.onClick}
                size="md"
                className="group py-2.5 shadow-[0_10px_30px_rgb(234_88_12/0.4)] sm:py-[0.8125rem]"
              >
                {action.label}
                <ArrowRight
                  aria-hidden="true"
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </CtaButton>
              {action.note && (
                <p className="mt-2 hidden max-w-[12.5rem] text-center text-[0.71875rem] leading-[1.4] text-balance text-white/50 lg:block">
                  {action.note}
                </p>
              )}
            </div>

            <button
              data-dock-item
              type="button"
              onClick={toggle}
              aria-expanded
              aria-label="Riduci"
              className="hidden h-8 w-8 shrink-0 place-items-center self-start rounded-ds-sm text-white/45 ds-focus hover:bg-white/8 hover:text-white sm:grid"
            >
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

/**
 * Il dock «Prova Trovanorma» degli articoli, per chi non ha un account.
 *
 * Resta in fondo allo schermo per tutta la lettura e dice in una riga perché
 * provare: la notizia vale per tutti, il caso del cliente no, e Trovanorma ci
 * risponde con le fonti che si aprono come nell'articolo. Si può ridurre a una
 * pillola, non togliere. Sul telefono parte pillola e si apre da solo una volta
 * per pagina, dopo un paio di schermate. La CTA è quella del sito
 * (`useAskAction`, posizione `dock`); chi ha un account non lo vede e ha la
 * barra `NewsStickyCta`. L'articolo lo trova dall'id `NEWS_ARTICLE_ID`: la
 * pagina intorno è resa dal server, e un ref non ci passa.
 */
const NewsTrialDock = ({ slug }: DockProps) => {
  const { isSettled } = useSignupModeState();
  // Finché la modalità non è nota l'etichetta del bottone potrebbe cambiare: si aspetta
  if (!isSettled) return null;
  return <Dock slug={slug} />;
};

export default NewsTrialDock;
